<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useThrottleFn } from '@vueuse/core';
import type { Origin } from '@pkg/types/surpriseBag';

const props = defineProps<{
  options: Origin[];
  loading?: boolean;
  placeholder?: string;
  inputAriaLabel?: string;
  initialSearchValue?: string;
  lightIcon?: boolean;
  resultsPanelId?: string;
  textColor?: string;
}>();

const emit = defineEmits<{
  selected: [value: Origin | undefined];
  inputChange: [value: string];
  externalResults: [payload: { results: Origin[]; open: boolean; loading: boolean }];
}>();

const searchValue = ref('');
const inputRef = ref<HTMLInputElement | null>(null);

watch(
  () => props.initialSearchValue,
  (val) => {
    if (val) {
      searchValue.value = val;
    }
  },
  { immediate: true },
);

const displayText = computed(() => searchValue.value || props.placeholder || '\u00A0');

const showClear = computed(() => searchValue.value.trim().length > 0);
const open = computed(() => showClear.value);

watch(
  () => ({ open: open.value, results: props.options, loading: props.loading ?? false }),
  ({ open: isOpen, results, loading }) => {
    emit('externalResults', {
      results: isOpen ? results : [],
      open: isOpen,
      loading,
    });
  },
  { immediate: true, deep: true },
);

const emitSearch = useThrottleFn(() => emit('inputChange', searchValue.value), 400);

function onInput(e: Event) {
  const target = e.target as HTMLInputElement;
  searchValue.value = target.value;
  emitSearch();
}

function clear() {
  searchValue.value = '';
  emit('selected', undefined);
  emit('inputChange', '');
}

defineExpose({
  focusInput: () => inputRef.value?.focus(),
});
</script>

<template>
  <div
    class="nav-header-autocomplete"
    :class="{ 'nav-header-autocomplete--has-text': showClear }"
  >
    <svg
      class="nav-header-autocomplete__icon"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M16.0155 17.0501L10.2616 11.2962C9.76158 11.7091 9.18658 12.0321 8.53658 12.2655C7.88658 12.4988 7.21415 12.6155 6.51928 12.6155C4.81011 12.6155 3.3636 12.0237 2.17973 10.8401C0.995864 9.65655 0.403931 8.21041 0.403931 6.50169C0.403931 4.79296 0.995714 3.34628 2.17928 2.16167C3.36285 0.977066 4.809 0.384766 6.51773 0.384766C8.22645 0.384766 9.67311 0.976699 10.8577 2.16057C12.0423 3.34443 12.6347 4.79095 12.6347 6.50012C12.6347 7.21422 12.5148 7.89627 12.275 8.54627C12.0353 9.19627 11.7154 9.76165 11.3154 10.2424L17.0943 16.0213C17.2327 16.1571 17.302 16.3237 17.302 16.5212C17.302 16.7187 17.2237 16.8957 17.0672 17.0522C16.9237 17.1957 16.7488 17.2674 16.5424 17.2674C16.336 17.2674 16.1603 17.195 16.0155 17.0501ZM6.51928 11.1155C7.80775 11.1155 8.8991 10.6684 9.79333 9.77417C10.6876 8.87993 11.1347 7.78858 11.1347 6.50012C11.1347 5.21165 10.6876 4.1203 9.79333 3.22607C8.8991 2.33183 7.80775 1.88472 6.51928 1.88472C5.23081 1.88472 4.13946 2.33183 3.24523 3.22607C2.35101 4.1203 1.90391 5.21165 1.90391 6.50012C1.90391 7.78858 2.35101 8.87993 3.24523 9.77417C4.13946 10.6684 5.23081 11.1155 6.51928 11.1155Z"
        fill="currentColor"
      />
    </svg>
    <div class="nav-header-autocomplete__input-wrap">
      <span
        class="nav-header-autocomplete__mirror"
        aria-hidden="true"
      >{{ displayText }}</span>
      <!-- [html-validate-disable-next no-missing-references] -->
      <input
        ref="inputRef"
        type="text"
        autocomplete="off"
        :value="searchValue"
        :placeholder="placeholder"
        :aria-label="inputAriaLabel"
        :aria-controls="resultsPanelId"
        class="nav-header-autocomplete__input"
        @input="onInput"
      >
    </div>
    <button
      v-show="showClear"
      type="button"
      class="nav-header-autocomplete__clear"
      aria-label="Clear search"
      @click="clear"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 13.054L6.927 18.127a.4.4 0 01-.566 0 .4.4 0 010-.566L11.434 12 6.361 6.927a.4.4 0 010-.566.4.4 0 01.566 0L12 11.434l5.073-5.073a.4.4 0 01.566 0 .4.4 0 010 .566L12.566 12l5.073 5.073a.4.4 0 010 .566.4.4 0 01-.566 0L12 13.054z"
          fill="currentColor"
        />
      </svg>
    </button>
  </div>
</template>

<style scoped>
.nav-header-autocomplete {
  display: inline-flex;
  align-items: center;
  height: 40px;
  width: fit-content;
  max-width: 100%;
  padding-left: theme('spacing.0');
  padding-right: theme('spacing.0');
  border-radius: 100px;
  color: inherit;
  border: 0px solid transparent;
  transition:
    padding 0.3s ease-out,
    margin-right 0.3s ease-out,
    background 0.3s ease-out,
    box-shadow 0.3s ease-out,
    border-color 1s ease-out,
    backdrop-filter 0.3s ease-out,
    -webkit-backdrop-filter 0.3s ease-out;

  &:hover,
  &--has-text {
    padding-left: theme('spacing.8');
    padding-right: theme('spacing.8');
    margin-right: theme('spacing.16');
    /* glassmorphism effect like liquid-glass button */
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    border-color: rgba(255, 255, 255, 0.3);
    border-width: 1px;
  }

  &:hover &__icon,
  &--has-text &__icon {
    margin-left: theme('spacing.8');
  }

  &__icon {
    width: 0.700rem;
    height: 0.700rem;
    flex-shrink: 0;
    margin-top: 2px;
    color: inherit;
  }

  &__input-wrap {
    position: relative;
    display: inline-block;
    max-width: 250px;
    min-width: auto;
    vertical-align: middle;
  }

  &__mirror {
    display: block;
    min-width: 4ch;
    visibility: hidden;
    white-space: pre;
    padding: theme('spacing.8') theme('spacing.12');
    font-size: 0.875rem;
    line-height: 1.25rem;
    pointer-events: none;
    font-family: inherit;
  }

  &__input {
    position: absolute;
    left: 0;
    top: 0;
    width: 90%;
    height: 100%;
    box-sizing: border-box;
    padding: theme('spacing.8') theme('spacing.0') theme('spacing.8') theme('spacing.8');
    border: none;
    border-radius: 100px;
    font-size: 0.875rem;
    line-height: 1.25rem;
    outline: none;
    background: transparent !important;
    color: inherit !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &::placeholder {
      color: inherit;
      opacity: 1;
    }

    &:focus {
      background: transparent !important;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px transparent inset;
      box-shadow: 0 0 0 1000px transparent inset;
    }
  }

  &__clear {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      opacity: 0.5;
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
  }
}
</style>
